import { useState } from "react";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import CreatableSelect from "react-select/creatable";
import ReactSelect from "react-select";
import { validateArray } from "../../utils/validate";
import { FileManagerModal } from "./FileManagerModal";

const InputSubDataDocumentAdd = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();

  // Obtenemos los documentos ya registrados
  const {
    data: registeredDocs,
    loading,
    call: reloadDocs,
  } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });

  // Obtenemos la lista de opciones para el Dropdown (tipos de documento)
  const {
    data: docTypes,
    loading: loadTypes,
    call: reloadTypes,
  } = Request({
    urlApi: field.urlApi,
  });

  function handleForm(item = null) {
    openDialog(
      <div className="w-[600px] max-w-full">
        <FormAddDocument
          existingDoc={item}
          docTypes={docTypes}
          field={field}
          reload={reloadDocs}
          reloadTypes={reloadTypes}
          onClose={dialogClose}
        />
      </div>,
    );
  }

  function handleDelete(data) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Eliminar este documento y sus archivos?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={reloadDocs}
      />,
    );
  }

  function handleFiles(item) {
    openDialog(
      <FileManagerModal
        item={item}
        reload={reloadDocs}
        onClose={dialogClose}
      />,
    );
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <div className="flex justify-between items-center mb-3">
        <Label className="font-bold text-gray-700 my-0">
          {field.labelCustom || field.label}
        </Label>
        <Button onClick={() => handleForm(null)} className="px-3 py-1 text-sm">
          + Agregar Documento
        </Button>
      </div>

      {loading ? (
        <div className="w-full">
          <TableComplete
            header={[
              {
                name: "label",
                label: "Tipo de Documento",
                type: "custom",
                builder: (val, _, item) => (
                  <div className="flex flex-col">
                    <span className="font-medium text-blue-800">{val}</span>
                    <span className="text-[10px] text-gray-400 uppercase">
                      {item.description2}
                    </span>
                  </div>
                ),
              },
              {
                name: "description",
                label: "Nro. Referencia",
              },
              {
                name: "files",
                label: "Archivo",
                type: "custom",
                builder: (_, __, item) => {
                  const hasFiles =
                    item.description4 && item.description4 !== "[]";
                  return (
                    <div className="flex items-center gap-2">
                      <i
                        className={`fas fa-file-alt ${hasFiles ? "text-green-500" : "text-gray-300"}`}
                      ></i>
                      <span className="text-xs">
                        {hasFiles ? "Adjunto" : "Sin archivo"}
                      </span>
                    </div>
                  );
                },
              },
              {
                name: "add",
                type: "action",
                label: "Acciones",
                sticky: true,
                actions: [
                  {
                    label: "Gestionar Archivo",
                    icon: "fas fa-paperclip",
                    action: handleFiles,
                    color: "text-purple-600",
                  },
                  {
                    label: "Editar Texto",
                    icon: "fas fa-edit",
                    action: handleForm,
                    color: "text-blue-600",
                  },
                  {
                    label: "Eliminar",
                    icon: "fas fa-trash",
                    action: handleDelete,
                    color: "text-red-400",
                  },
                ],
              },
            ]}
            data={registeredDocs}
          />
        </div>
      ) : (
        <Circle />
      )}
    </div>
  );
};

const FormAddDocument = ({
  existingDoc,
  docTypes,
  field,
  reload,
  reloadTypes,
  onClose,
}) => {
  const [values, setValues] = useState({
    Label: existingDoc?.label || "",
    Description: existingDoc?.description || "", // Numero
    Description2: existingDoc?.description2 || "Original", // Formato
    Description4: existingDoc?.description4 || "[]", // Files IDs
    isNew: false,
  });

  async function handleSubmit() {
    if (!values.Label) return toast.error("Seleccione el tipo de documento");

    try {
      // Si el usuario escribió un documento nuevo en el CreatableSelect
      if (values.isNew) {
        await requestAuth("post", "/Dropdown/Option", {
          Name: values.Label,
          DropdownListLabel: field.urlInitial,
        });
        reloadTypes();
      }

      const payload = {
        ...values,
        dataSetId: field.dataSetId,
      };

      await requestAuth(
        existingDoc ? "put" : "post",
        existingDoc
          ? `/dataSet/procedure/subData/${existingDoc.id}`
          : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
        payload,
      );

      toast.success("Datos guardados correctamente");
      reload();
      onClose();
    } catch (err) {
      toast.error("Error al guardar");
    }
  }

  return (
    <div className="flex flex-col gap-4 p-5 bg-white rounded-lg shadow-xl">
      <H3 className="border-b pb-2 text-blue-700">
        {existingDoc ? "Actualizar Documento" : "Nuevo Registro de Documento"}
      </H3>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Tipo de Documento</Label>
          <CreatableSelect
            isSearchable
            placeholder="Ej: Factura, Certificado, etc."
            onChange={(e) =>
              setValues({
                ...values,
                Label: e?.value || "",
                isNew: e?.__isNew__ || false,
              })
            }
            value={
              values.Label ? { label: values.Label, value: values.Label } : null
            }
            options={
              validateArray(docTypes)
                ? docTypes.map((d) => ({ value: d.name, label: d.name }))
                : []
            }
            formatCreateLabel={(v) => `Agregar "${v}" a la lista`}
          />
        </div>

        <div>
          <Label>Número / Referencia</Label>
          <input
            className="w-full p-2 border rounded outline-none focus:ring-2 focus:ring-blue-200"
            value={values.Description}
            onChange={(e) =>
              setValues({ ...values, Description: e.target.value })
            }
            placeholder="Nro de documento..."
          />
        </div>

        <div>
          <Label>Formato</Label>
          <ReactSelect
            options={[
              { value: "Original", label: "Original" },
              { value: "Fotocopia", label: "Fotocopia" },
              { value: "Copia Legalizada", label: "Copia Legalizada" },
            ]}
            value={{ value: values.Description2, label: values.Description2 }}
            onChange={(e) => setValues({ ...values, Description2: e.value })}
          />
        </div>
      </div>

      <div className="bg-blue-50 p-3 rounded text-xs text-blue-600 italic">
        Nota: Primero guarde la información del documento. Una vez creado, podrá
        adjuntar el archivo PDF/Imagen usando el icono del <b>clip (clip)</b> en
        la tabla principal.
      </div>

      <div className="flex justify-end gap-3 mt-2">
        <button onClick={onClose} className="text-gray-500 font-medium px-4">
          Cancelar
        </button>
        <Button onClick={handleSubmit}>Confirmar Registro</Button>
      </div>
    </div>
  );
};

export default InputSubDataDocumentAdd;
